import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { localize } from "redux-i18n";
import moment from "moment";
import classNames from "classnames";
import Icons from "../icons";
import { Button, ComboBox, type OptionType, View } from "@components";
import config from "@config";
import "./style.scss";

const supportedDateFormats = [
  "DD MM YYYY",
  "DD MM YY",
  "DD MMM YYYY",
  "DD MMM YY",
  "DD-MM-YYYY",
  "DD-MM-YY",
  "DD-MM-YYYY HH:mm",
  "DD-MM-YY HH:mm",
  "DD/MM/YYYY",
  "DD/MM/YY",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YY HH:mm",
  "DD.MM.YYYY",
  "DD.MM.YY",
  "DD.MM.YYYY HH:mm",
  "DD.MM.YY HH:mm",
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm",
  "YYYY/MM/DD",
  "YYYY/MM/DD HH:mm",
  "YYYY.MM.DD",
  "YYYY.MM.DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
];
const yyyymmddISO8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.?\d{3}?Z?$/g; // YYYY-MM-DDTHH:mm:ss.SSSZ
const ddmmyyRegex = /^(0?[1-9]|[1-2][0-9]|3[0-1])[\/\.-](0?[1-9]|1[0-2])[\/\.-]([0-9]{2}|[0-9]{4})$/g; // DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY
const yyyymmddRegex = /^([0-9]{2}|[0-9]{4})[\/\.-](0?[1-9]|1[0-2])[\/\.-](0?[1-9]|[1-2][0-9]|3[0-1])$/g; // YYYY-MM-DD or YYYY/MM/DD or YYYY.MM.DD

export type Props = $Exact<{
  id: string,
  placeholder?: string,
  isToggled?: any,
  value?: string,
  rawDateFormat?: string,
  displayDateFormat?: string,
  maxDate?: string,
  minDate?: string,
  disabled?: boolean,
  hideResetButton?: boolean,
  readonly?: boolean,
  required?: boolean,
  className?: string,
  enableTime?: boolean,
  enableDate?: boolean,
  locale?: string,
  onChange: Function,
  onError: Function,
  onReset?: Function,
}>;

export type State = $Exact<{
  id: string,
  rawDateFormat: string,
  displayDateFormat?: string,
  inputPlaceholder: string,
  inputMaxDate: moment$Moment | null, // yyyy-mm-dd
  inputMinDate: moment$Moment | null, // yyyy-mm-dd
  enableTime: boolean,
  enableDate: boolean, // Currently unused but should implement this, so can be just time picker if this is false
  errorMessage: string,
  locale?: string,
  enableManualInput?: boolean,
}>;

export default function DatePicker(props: Props) {
  const datePickerRef: HTMLDivElement = useRef(null);
  const calendarRef: HTMLDivElement = useRef(null);
  const inputRef: HTMLInputElement = useRef(null);

  const valueDate = props.value ? parseDateString(props.value) : null;

  const [state, setState]: [State, Function] = useState({
    id: props.id,
    errorMessage: false,
    repeatCallTimer: null,
  });

  const enableTime = !!props.enableTime || false;
  const enableDate = !!props.enableDate || true; // Should implement this, so can be just time picker if this is false
  const errorMessage = state.errorMessage;
  const enableManualInput = false; // Should implement such functionality
  const locale = props.locale || "en";
  const displayDateFormat = !!props.enableTime ? config.dateTimeFormat : config.dateFormat; // a bit confusing but .;
  const rawDateFormat = !!props.enableTime ? config.dateTimeFormatBE : config.dateFormatBE;
  const inputPlaceholder = props.placeholder || "";
  const inputMaxDate = props.maxDate ? moment(props.maxDate, supportedDateFormats) : null;
  const inputMinDate = props.minDate ? moment(props.minDate, supportedDateFormats) : null;

  const [selectedDate, setSelectedDate]: [moment$Moment | null, Function] = useState(valueDate);

  const [innerPointerDate, setInnerPointerDate]: [moment$Moment, Function] = useState(
    valueDate !== null ? valueDate : moment(),
  );

  const [inputValue, setInputValue]: [string, Function] = useState(
    valueDate ? valueDate.format(displayDateFormat) : "",
  );

  const [position, setPosition]: [{ top: number, left: number }, Function] = useState(null);
  const [offset, setOffset]: [{ x: number, y: number }, Function] = useState({ x: 0, y: 0 });

  useEffect(() => {
    props.onError && props.onError(errorMessage);
  }, [errorMessage]);

  useEffect(() => {
    document.body && document.body.addEventListener("mousedown", isOutsideClick);
    const t = setTimeout(() => {
      if (position && calendarRef.current) {
        const outOfBounds = getComponentOutOfBounds(calendarRef);
        if (outOfBounds.x || outOfBounds.y) {
          setOffset(_ => {
            return { x: outOfBounds.x || 0, y: outOfBounds.y || 0 };
          });
        }
      }
    }, 1);
    return () => {
      document.body && document.body.removeEventListener("mousedown", isOutsideClick);
      clearTimeout(t);
    };
  }, [position]);

  useEffect(() => {
    const valueDate = props.value && props.value !== "" ? parseDateString(props.value) : null;
    setSelectedDate(valueDate);
    setInnerPointerDate(valueDate || moment());
    setInputValue(valueDate ? valueDate.format(displayDateFormat) : "");
  }, [props.value]);

  useEffect(() => {
    const selDate = typeof selectedDate == "string" ? moment(selectedDate) : selectedDate;
    //$FlowIgnore
    setInnerPointerDate(selDate?.isValid() ? selDate : moment());
    setInputValue(selDate && selDate.isValid() ? selDate.format(displayDateFormat) : "");
    selDate && validateDate(selDate);
  }, [selectedDate]);

  function getComponentOutOfBounds(ref): Object {
    if (!ref || !ref.current) return { x: 0, y: 0 };
    const rect = ref.current.getBoundingClientRect();
    const { innerHeight, innerWidth } = window;

    let res = {};
    if (rect.x + rect.width > innerWidth) {
      res.right = rect.width + rect.x - innerWidth;
      res.x = -res.right;
    }
    if (rect.x < 0) {
      res.left = rect.x;
      res.x = -res.left;
    }
    if (rect.top < 0) {
      res.top = rect.top;
      res.y = res.top;
    }
    if (rect.y + rect.height > innerHeight) {
      res.bottom = rect.y + rect.height - innerHeight;
      res.y = -res.bottom;
    }

    return res;
  }
  function handleScrollWhileToggled() {
    if (position) {
      displayTheDatePicker();
    }
  }
  function handleClickOutside(event: MouseEvent) {
    event.stopPropagation();
    if (position === null || !calendarRef.current) return;

    const { clientX, clientY } = event;
    const { top, right, bottom, left } = calendarRef.current.getBoundingClientRect();

    if (clientX <= right && clientX >= left && clientY >= top && clientY <= bottom) {
      //cliecked inside
    } else {
      toggleDatepicker();
    }
  }
  function isOutsideClick(event: React.MouseEvent) {
    if (!calendarRef.current) return;
    const containerRect = calendarRef.current.getBoundingClientRect();
    const isMouseInsideContainer =
      event.clientX >= containerRect.x &&
      event.clientX <= containerRect.x + containerRect.width &&
      event.clientY >= containerRect.y &&
      event.clientY <= containerRect.y + containerRect.height;

    if (isMouseInsideContainer) {
      return false;
    } else {
      toggleDatepicker();
    }
  }
  function getComponentOffset(ref): { x: number, y: number } {
    let res = { x: 0, y: 0 };
    if (!ref || !ref.current) return res;
    const rect = ref.current.getBoundingClientRect();
    const posX = rect.left + rect.width;
    const posY = rect.top + rect.height;

    const right = posX > window.innerWidth;
    const bottom = posY > window.innerHeight;

    if (right) res.x = Math.ceil(posX - window.innerWidth);
    if (bottom) res.y = Math.ceil(posY - window.innerHeight);

    return res;
  }
  function toggleDatepicker(e?: SyntheticMouseEvent<HTMLDivElement>) {
    e && e.stopPropagation();
    if (!position) {
      displayTheDatePicker();
    } else {
      hideTheDatePicker();
    }
  }
  function displayTheDatePicker(
    position?: {
      top: number,
      right: number,
      width: number,
      height: number,
    } | null = null,
  ) {
    if (position === null) {
      position = datePickerRef.current.getBoundingClientRect();
    }

    const { top, right, width, height } = position;
    setPosition(_ => {
      return {
        left: right - width /** 330px is the min width of this el */,
        top: top + height /** moving 25px down */,
      };
    });
  }
  function hideTheDatePicker() {
    setPosition(null);
    setOffset({ x: 0, y: 0 });
  }
  function generateMonthDropdown() {
    const months = moment.monthsShort().map((month: string, idx: number) => {
      return { value: idx, label: month };
    });
    const selectedMonthIdx = moment(innerPointerDate).month();
    return (
      <ComboBox
        className="ml-1"
        options={months}
        value={months[selectedMonthIdx]}
        onChange={handleMonthChange}
        withoutEmptyOption
      />
    );
  }
  function generateYearDropdown() {
    const yearsBack = 100;
    const year = new Date().getFullYear() + 10;
    const years = Array.from(new Array(yearsBack), (val, index) => year - index);
    return (
      <ComboBox
        className="mr-1 ml-1"
        options={years.map((year, index) => {
          return year;
        })}
        value={{
          value: moment(innerPointerDate).year(),
          label: moment(innerPointerDate).year(),
        }}
        onChange={handleYearChange}
        withoutEmptyOption
      />
    );
  }
  function generateCalendar() {
    const filterYear = moment(innerPointerDate).year();
    const filterMonth = moment(innerPointerDate).month();

    const nowYear = moment().year();
    const nowMonth = moment().month();
    const nowDay = moment().date();

    const selectedYear = (selectedDate || moment()).year();
    const selectedMonth = (selectedDate || moment()).month();
    const selectedDay = (selectedDate || moment()).date();

    const firstDay = (new Date(filterYear, filterMonth).getDay() - 1 + 7) % 7;

    const daysInMonth = 32 - new Date(filterYear, filterMonth, 32).getDate();

    let date = 1;
    let weeks = [];
    let days = [];
    let day = { className: null, text: null };

    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 7; j++) {
        const monthFormatted = (filterMonth + 1).toString().padStart(2, "0"); // starts counting from 0
        const dayFormatted = (date + 1).toString().padStart(2, "0"); // starts counting from 0
        const yearFormatted = filterYear;
        const dateString = `${yearFormatted}-${monthFormatted}-${dayFormatted}`;
        if (i === 0 && j < firstDay) {
          day = { className: "", text: "" };
        } else if (date > daysInMonth) {
          break;
        } else {
          const dString = moment(
            `${innerPointerDate.format("YYYY-MM")}-${date
              .toString()
              .padStart(2, "0")} ${(inputMinDate && inputMinDate.hour()) || ""}:${(inputMinDate &&
              inputMinDate.minute()) ||
              ""}`,
          );

          // const dString = moment(innerPointerDate.format(`YYYY-MM-${dayFormatted} 00:00`));

          const isBeforeMin = inputMinDate && dString.isBefore(inputMinDate);
          const isSameOrAfterMax = inputMaxDate && dString.isSameOrAfter(inputMaxDate);

          if (
            selectedYear === filterYear &&
            selectedMonth === filterMonth &&
            selectedDay === date &&
            !isBeforeMin &&
            !isSameOrAfterMax
          ) {
            day = { text: date, className: "selected-date" };
          } else if (isBeforeMin) {
            day = {
              text: date,
              className: "text-color-pinkish-grey no-pointer-events is-same-or-before-min",
            };
          } else if (isSameOrAfterMax) {
            day = {
              text: date,
              className: "text-color-pinkish-grey no-pointer-events is-same-or-after-max",
            };
          } else {
            day = { text: date, className: "selectable" };
          }

          if (nowYear === filterYear && nowMonth === filterMonth && nowDay === date) {
            day.className += " today";
          }

          date++;
        }
        days.push(day);
      }

      weeks.push(days);
      days = [];
    }
    // Making  the weekdays starts from  Mon
    const weekdaysShort = moment.weekdaysShort();
    const weekdaysShortMon = [...weekdaysShort];
    weekdaysShortMon.push(weekdaysShortMon.shift());
    return (
      <table>
        <thead>
          <tr>
            {weekdaysShortMon.map(month => (
              <th key={month}>{month}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, widx) => (
            <tr key={widx}>
              {week.map((day, didx) => (
                <td
                  key={`${widx}-${didx}`}
                  className={day.className}
                  data-date={`${filterYear}-${filterMonth + 1}-${day.text}`}
                  onClick={handleDayClick}
                  onDoubleClick={handleApply}
                >
                  {day.text}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  function handleYearChange(val: OptionType) {
    setInnerPointerDate(moment(innerPointerDate).year(parseInt(val)));
  }
  function handleMonthChange(val: OptionType) {
    setInnerPointerDate(moment(innerPointerDate).month(parseInt(val)));
  }
  function handleDayClick(e: SyntheticMouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    if (!e.target.classList.contains("selectable")) return;

    const newDate = parseDateString(e.currentTarget.dataset.date);

    if (selectedDate && selectedDate.hour && selectedDate.minute && selectedDate.second) {
      newDate.hour(selectedDate.hour());
      newDate.minute(selectedDate.minute());
      newDate.second(selectedDate.second());
    } else {
      newDate.hour(moment().hour());
      newDate.minute(moment().minute());
      newDate.second(moment().second());
    }

    setSelectedDate(newDate);
    if (!enableTime) {
      toggleDatepicker();
    }
    applyToParent(newDate);
  }

  function handleYesterdayClick() {
    const date = moment().subtract(1, "days");
    setState({
      ...state,
      errorMessage: false,
    });
    const vDate = validateDate(date.toISOString());
    setSelectedDate(date);
    applyToParent(vDate.date);
  }

  function handleTomorrowClick() {
    const date = moment().add(1, "day");

    setState({
      ...state,
      errorMessage: false,
    });
    const vDate = validateDate(date.toISOString());
    setSelectedDate(date);
    applyToParent(vDate.date);
  }

  function handleTodayClick() {
    const date = moment();
    setState({
      ...state,
      errorMessage: false,
    });
    const vDate = validateDate(date.toISOString());
    setSelectedDate(date);
    applyToParent(vDate.date);
  }
  function handleNextMonthClick(e: MouseEvent) {
    e.stopPropagation();
    setInnerPointerDate(moment(innerPointerDate).add(1, "months"));
  }
  function handlePrevMonthClick(e: MouseEvent) {
    e.stopPropagation();
    setInnerPointerDate(moment(innerPointerDate).subtract(1, "months"));
  }
  function handleNextYearClick(e: MouseEvent) {
    e.stopPropagation();
    setInnerPointerDate(moment(innerPointerDate).add(1, "years"));
  }
  function handlePrevYearClick(e: MouseEvent) {
    e.stopPropagation();
    setInnerPointerDate(moment(innerPointerDate).subtract(1, "years"));
  }
  function handleRepeatCall(action: string) {
    return function() {
      setSelectedDate(d => {
        const date = moment(d || new Date());
        switch (action) {
          case "hourUp":
            date.add(1, "hours");
            break;
          case "hourDown":
            date.subtract(1, "hours");
            break;
          case "minuteUp":
            date.add(1, "minutes");
            break;
          case "minuteDown":
            date.subtract(1, "minutes");
            break;
        }
        const valD = validateDate(date.toISOString().toString());
        return valD.isValid ? date : valD.date;
      });
    };
  }
  function getValidHours(value: string) {
    // Max hours is 23
    const hours = parseInt(value);

    if (hours > 23) return value.slice(-1);
    else return hours;
  }
  function getValidMinutes(value: string) {
    // Max minutes is 59
    const minutes = parseInt(value);
    if (minutes > 59) return value.slice(-1);
    else return minutes;
  }

  function handleHoursChange(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    setSelectedDate(moment(innerPointerDate).hours(getValidHours(e.currentTarget.value)));
    setInnerPointerDate(moment(innerPointerDate).hours(getValidHours(e.currentTarget.value)));
  }
  function handleMinutesChange(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    setSelectedDate(moment(innerPointerDate).minutes(getValidMinutes(e.currentTarget.value)));
    setInnerPointerDate(moment(innerPointerDate).minutes(getValidMinutes(e.currentTarget.value)));
  }
  function applyOnEnter(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (e.charCode === 13) handleApply(); // Submit on pressing Enter
  }
  function handleNowClick(e: MouseEvent) {
    const date = moment(innerPointerDate)
      .minutes(moment().minutes())
      .hours(moment().hours());
    setInnerPointerDate(date);
    setSelectedDate(date);
  }
  function handleFocusInput(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (props.readonly || props.disabled) return;
    e.currentTarget.select();
    validateDate(inputValue);
    setState({ ...state, errorMessage: false });
  }
  function handleManualChange(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (props.readonly || props.disabled) return;
    setInputValue(e.currentTarget.value);
  }
  function handleBlur(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (props.readonly || props.disabled) return;
    if (inputValue === "") {
      clearDate(e);
    } else {
      const isValid = validateDate(inputValue).isValid;
      if (isValid === true) {
        const date = parseDateString(inputValue);
        setSelectedDate(date);
        applyToParent(date);
      }
    }
  }
  function handleOnKeyPress(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (props.readonly || props.disabled) return;
    if (e.ctrlKey === true && e.charCode === 13 && e.currentTarget.value === "") {
      setSelectedDate(moment());
      setState({
        ...state,
        errorMessage: false,
      });
    } else if (e.shiftKey === true && e.ctrlKey === true && e.charCode === 13) {
      clearDate(e);
    } else if (e.charCode === 13) {
      setSelectedDate(parseDateString(e.currentTarget.value));
      applyToParent(parseDateString(e.currentTarget.value));
    }
  }

  function handleApply() {
    if (selectedDate || innerPointerDate) {
      applyToParent(selectedDate || innerPointerDate);
      toggleDatepicker();
    }
  }

  function applyToParent(momentDate: moment$Moment | "") {
    if (typeof momentDate == "object" && !momentDate.isValid()) return;
    if (typeof momentDate == "string" && momentDate == "")
      return props.onChange && props.onChange(undefined);
    if (typeof momentDate == "object" && momentDate.isValid())
      return props.onChange && props.onChange(momentDate.format(rawDateFormat));
  }

  function handleCancel(e: MouseEvent) {
    toggleDatepicker();
  }

  function clearDate(e: SyntheticEvent<HTMLElement>) {
    e.stopPropagation();
    setSelectedDate(null);
    setInputValue("");
    applyToParent("");
    setState({ ...state, errorMessage: false });
    if (props.onReset) {
      props.onReset();
    }
  }

  function selectAll(e: SyntheticMouseEvent<HTMLInputElement>) {
    e.currentTarget.select();
  }

  function parseDateString(date: string): moment$Moment {
    return moment(date, supportedDateFormats);
  }
  function validateDate(date: moment$Moment | string): { isValid: boolean, date: moment$Moment } {
    const mDate = typeof date === "string" ? parseDateString(date) : date;

    if (!mDate.isValid()) {
      setState({
        ...state,
        errorMessage:
          "The input must be a valid date in the format DD/MM/YY(YY) DD.MM.YY(YY) DD-MM-YY(YY)",
      });
      return { isValid: false, date: moment() };
    }
    // If the date format is ok, we can convert it to moment
    if (inputMaxDate && mDate && mDate.isAfter(inputMaxDate)) {
      setState({
        ...state,
        errorMessage: `The input must be a date not later than ${moment(inputMaxDate).format(
          displayDateFormat,
        )}`,
      });
      return { isValid: false, date: inputMaxDate };
    } else if (inputMinDate && mDate && mDate.isBefore(inputMinDate)) {
      setState({
        ...state,
        errorMessage: `The input must be a date not earlier than ${moment(inputMinDate).format(
          displayDateFormat,
        )}`,
      });
      return { isValid: false, date: inputMinDate };
    } else {
      setState({ ...state, errorMessage: false });
      return { isValid: true, date: mDate };
    }
  }

  function formatHours() {
    if (!selectedDate) return "00";
    let hours = selectedDate.hours();
    return hours < 10 ? `${0}${hours}` : `${hours}`;
  }
  function formatMinutes() {
    if (!selectedDate) return "00";
    let minutes = selectedDate.minutes();
    return minutes < 10 ? `${0}${minutes}` : `${minutes}`;
  }

  return (
    <div
      className={classNames("main-container", props.className, {
        invalid: errorMessage,
        "form-input--required": props.required && !props.value,
        readonly: props.readonly,
        disabled: props.disabled,
      })}
      ref={datePickerRef}
    >
      <input
        data-inner-pointer={innerPointerDate?.toISOString()}
        data-selected-date={selectedDate?.toISOString()}
        data-min-date={inputMinDate?.toISOString()}
        data-max-date={inputMaxDate?.toISOString()}
        data-value={valueDate?.toISOString()}
        ref={inputRef}
        type="text"
        className={"form-control"}
        disabled={props.disabled}
        readOnly={props.readonly}
        value={inputValue}
        placeholder={props.placeholder}
        onChange={handleManualChange}
        onBlur={handleBlur}
        onFocus={handleFocusInput}
        onKeyPress={handleOnKeyPress}
        id={props.id || `date-picker-${new Date().getTime().toString()}`}
      />
      {inputValue && !props.readonly && !props.disabled && !props.hideResetButton && (
        <div
          className="icon clear position-absolute pointer"
          onClick={props.disabled ? null : clearDate}
        >
          {<span className="material-icons">cancel</span>}
        </div>
      )}
      {!props.readonly && !props.disabled && (
        <div
          className="icon calendar position-absolute"
          onClick={!props.disabled && toggleDatepicker}
        >
          <span className="material-icons pointer">calendar_month</span>
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      {position &&
        createPortal(
          <div
            ref={calendarRef}
            className={`date-time-picker `}
            style={{
              position: !!position ? "fixed" : "relative",
              top: position?.top ? `${position.top + offset.y}px` : "auto",
              left: position?.left ? `${position.left + offset.x}px` : "auto",
            }}
          >
            <div className="calendar-filter-picker-container">
              <div className="month-year-filter-container">
                <div
                  onClick={handlePrevYearClick}
                  className="material-icons text-color-charcoal-dark cursor-pointer d-flex align-items-center"
                >
                  arrow_back_ios
                </div>
                <div
                  onClick={handlePrevMonthClick}
                  className="material-icons text-color-charcoal-dark cursor-pointer d-flex align-items-center"
                >
                  chevron_left
                </div>
                {generateMonthDropdown()}
                {generateYearDropdown()}
                <div
                  onClick={handleNextMonthClick}
                  className="material-icons text-color-charcoal-dark cursor-pointer d-flex align-items-center"
                >
                  navigate_next
                </div>
                <div
                  onClick={handleNextYearClick}
                  className="material-icons text-color-charcoal-dark cursor-pointer d-flex align-items-center"
                >
                  arrow_forward_ios
                </div>
              </div>
              <div className="calendar-container">{generateCalendar()}</div>
              <div className="flex-column flex-wrap">
                <div className="days-quick-select-container d-flex justify-content-between">
                  <Button size={"small"} onClick={handleYesterdayClick}>
                    Yesterday
                  </Button>
                  <Button size={"small"} onClick={handleTodayClick}>
                    Today
                  </Button>
                  <Button size={"small"} onClick={handleTomorrowClick}>
                    Tomorrow
                  </Button>
                </div>
              </div>
            </div>
            {enableTime && (
              <div className={`hour-minute-picker-container ${enableTime ? "visible" : ""}`}>
                <h6 className="flex-grow-2 text-center">Time</h6>

                <div className="btns-container">
                  <div
                    onClick={handleRepeatCall("hourUp")}
                    className="material-icons cursor-pointer w-50-px text-center"
                  >
                    keyboard_double_arrow_up
                  </div>
                  <div
                    onClick={handleRepeatCall("minuteUp")}
                    className="material-icons cursor-pointer w-50-px text-center"
                  >
                    keyboard_double_arrow_up
                  </div>
                </div>
                <div className="inputs-container">
                  <input
                    type="text"
                    className={"clock"}
                    value={innerPointerDate ? innerPointerDate.format("HH") : "00"}
                    onClick={selectAll}
                    onChange={handleHoursChange}
                    onBlur={handleHoursChange}
                    onKeyPress={applyOnEnter}
                  />
                  <span className="separator">:</span>
                  <input
                    type="text"
                    className={"clock"}
                    value={innerPointerDate ? innerPointerDate.format("mm") : "00"}
                    onClick={selectAll}
                    onChange={handleMinutesChange}
                    onBlur={handleMinutesChange}
                    onKeyPress={applyOnEnter}
                  />
                </div>
                <div className="btns-container">
                  <span />
                  <div
                    onClick={handleRepeatCall("hourDown")}
                    className="material-icons cursor-pointer w-50-px text-center"
                  >
                    keyboard_double_arrow_down
                  </div>
                  <div
                    onClick={handleRepeatCall("minuteDown")}
                    className="material-icons cursor-pointer w-50-px text-center"
                  >
                    keyboard_double_arrow_down
                  </div>
                </div>
                <div className="btns-container flex-grow-2">
                  <Button color={"secondary"} onClick={handleNowClick}>
                    Now
                  </Button>
                </div>
                <div className="btns-container">
                  <Button size={"small"} color={"negative"} onClick={toggleDatepicker}>
                    Cancel
                  </Button>
                  <Button size={"small"} color={"default"} onClick={handleApply}>
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
